import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { motion } from 'framer-motion';
import { Mail, ArrowLeft, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await api.post('/auth/forgot-password', { email: email.trim().toLowerCase() });
      setEmailSent(true);
      toast.success('Password reset link sent to your email!');
    } catch (error) {
      toast.error('Failed to send reset link. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#0F172A] to-[#1E3A8A] px-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="w-full max-w-md"
      >
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="text-center mb-8">
            <img
              src="https://customer-assets.emergentagent.com/job_gbaian-hub/artifacts/hv9nezmf_GBA%20LOGO%20PNG.png"
              alt="GBA Logo"
              className="h-20 mx-auto mb-4"
            />
            <h1 className="font-oswald text-3xl font-bold text-[#1E3A8A] uppercase tracking-tight">
              Forgot Password?
            </h1>
            <p className="text-slate-600 mt-2">We'll send you a reset link</p>
          </div>

          {!emailSent ? (
            <form onSubmit={handleSubmit} data-testid="forgot-password-form">
              <div className="mb-6">
                <Label htmlFor="email" className="text-sm font-semibold text-slate-700">
                  Email Address
                </Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  className="mt-1 focus:ring-2 focus:ring-green-500"
                  data-testid="email-input"
                  placeholder="your@email.com"
                />
                <p className="text-xs text-slate-500 mt-2">
                  Enter the email address associated with your account
                </p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#22C55E] hover:bg-[#16A34A] text-white font-bold py-3 shadow-lg shadow-green-500/20"
                data-testid="send-reset-link-button"
              >
                {loading ? 'Sending...' : 'Send Reset Link'}
              </Button>
            </form>
          ) : (
            <Card className="p-6 bg-green-50 border-green-200">
              <div className="flex items-start gap-4">
                <div className="p-3 bg-[#22C55E] rounded-full">
                  <CheckCircle className="h-6 w-6 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-2">Check Your Email!</h3>
                  <p className="text-sm text-slate-700 mb-3">
                    If an account exists with <strong>{email}</strong>, we've sent a password reset link.
                  </p>
                  <p className="text-sm text-slate-600">
                    The link will expire in <strong>1 hour</strong>. Check your inbox and spam folder.
                  </p>
                </div>
              </div>
            </Card>
          )}

          <div className="mt-6">
            <Link to="/login">
              <Button
                variant="outline"
                className="w-full"
                data-testid="back-to-login-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Login
              </Button>
            </Link>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-white/70">
            ENHANCE • ELEVATE • EXCEL
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default ForgotPassword;
